import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import QRCode from "qrcode";
import type { EventAdminDTO, MediaDTO } from "@wedding/shared-types";
import { Layout } from "../components/common/Layout.js";
import { Spinner } from "../components/common/Spinner.js";
import {
  getAdminEvent,
  updateAdminEvent,
  deleteAdminEvent,
  getAdminConfig,
  listAdminEventMedia,
  deleteAdminMedia,
  adminThumbnailUrl,
} from "../api/admin.js";
import { ApiClientError } from "../api/client.js";

function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

export function AdminEventEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventAdminDTO | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [uploadsEnabled, setUploadsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [media, setMedia] = useState<MediaDTO[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);
  // Synchronous guards — see the identical comment in GalleryPage.tsx:
  // `loadingMedia`/`cursor`/`hasMore` state only reflect reality on the
  // *next* render, which lets React StrictMode's double-invoked mount
  // effect (dev only) fetch and append the same page twice.
  const loadingMediaRef = useRef(false);
  const cursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(true);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [ev, config] = await Promise.all([getAdminEvent(id), getAdminConfig()]);
      setEvent(ev);
      setPublicUrl(config.publicUrl);
      setName(ev.name);
      setExpiresAt(toDateInputValue(ev.expiresAt));
      setIsActive(ev.isActive);
      setUploadsEnabled(ev.uploadsEnabled);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate("/admin/login");
        return;
      }
      throw err;
    }
  }, [id, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!event || !publicUrl) return;
    const link = `${publicUrl}/m/${event.shareToken}`;
    void QRCode.toDataURL(link, { width: 320, margin: 1 }).then(setQrDataUrl);
  }, [event, publicUrl]);

  const loadMedia = useCallback(async () => {
    if (!id || loadingMediaRef.current || !hasMoreRef.current) return;
    loadingMediaRef.current = true;
    setLoadingMedia(true);
    try {
      const page = await listAdminEventMedia(id, { cursor: cursorRef.current, limit: 60 });
      setMedia((prev) => [...prev, ...page.items]);
      cursorRef.current = page.nextCursor;
      hasMoreRef.current = page.nextCursor !== null;
      setHasMore(hasMoreRef.current);
    } finally {
      loadingMediaRef.current = false;
      setLoadingMedia(false);
    }
  }, [id]);

  useEffect(() => {
    void loadMedia();
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const updated = await updateAdminEvent(id, {
        name,
        password: password.trim() ? password : undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        isActive,
        uploadsEnabled,
      });
      setEvent(updated);
      setPassword("");
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.message : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteEvent() {
    if (!id || !event) return;
    if (!window.confirm(`Supprimer définitivement "${event.name}" et tous ses médias ?`)) return;
    await deleteAdminEvent(id);
    navigate("/admin");
  }

  async function handleDeleteMedia(mediaId: string) {
    if (!window.confirm("Supprimer ce média définitivement ?")) return;
    await deleteAdminMedia(mediaId);
    setMedia((prev) => prev.filter((m) => m.id !== mediaId));
  }

  function handleCopyLink() {
    if (!event || !publicUrl) return;
    void navigator.clipboard.writeText(`${publicUrl}/m/${event.shareToken}`);
  }

  function handleDownloadQr() {
    if (!qrDataUrl || !event) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `qr-${event.shareToken}.png`;
    a.click();
  }

  if (!event) return <Spinner label="Chargement…" />;

  const shareLink = publicUrl ? `${publicUrl}/m/${event.shareToken}` : "";

  return (
    <Layout
      title={event.name}
      actions={
        <Link to="/admin" className="btn btn-secondary">
          Retour
        </Link>
      }
    >
      <div className="stack">
        <div className="card stack">
          <h2>Partage</h2>
          <p style={{ wordBreak: "break-all" }}>{shareLink}</p>
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
              Copier le lien
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleDownloadQr} disabled={!qrDataUrl}>
              Télécharger le QR code
            </button>
          </div>
          {qrDataUrl && (
            <img src={qrDataUrl} alt="QR code du lien de la galerie" style={{ width: 200, height: 200 }} />
          )}
        </div>

        <form className="card stack" onSubmit={handleSave}>
          <h2>Réglages</h2>
          <div className="field">
            <label htmlFor="edit-name">Nom</label>
            <input id="edit-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="edit-password">Nouveau mot de passe (laisser vide pour ne pas changer)</label>
            <input
              id="edit-password"
              className="input"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="edit-expires">Date d'expiration</label>
            <input
              id="edit-expires"
              className="input"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Événement actif (accessible aux invités)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <input
              type="checkbox"
              checked={uploadsEnabled}
              onChange={(e) => setUploadsEnabled(e.target.checked)}
            />
            Autoriser les envois d'invités
          </label>
          {saveError && <p className="error-text">{saveError}</p>}
          {saveSuccess && <span className="badge badge-success">Enregistré</span>}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </form>

        <div className="card">
          <h2>Statistiques</h2>
          <p>
            {event.photoCount} photo(s) · {event.videoCount} vidéo(s) ·{" "}
            {(event.totalSizeBytes / (1024 * 1024)).toFixed(1)} Mo utilisés
          </p>
        </div>

        <div className="stack">
          <h2>Médias</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: "var(--space-2)",
            }}
          >
            {media.map((m) => (
              <div key={m.id} style={{ position: "relative" }}>
                {m.thumbnailStatus === "ready" ? (
                  <img
                    src={adminThumbnailUrl(m.id)}
                    alt=""
                    loading="lazy"
                    style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "var(--radius-sm)" }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      background: "var(--color-border)",
                      borderRadius: "var(--radius-sm)",
                    }}
                  />
                )}
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteMedia(m.id)}
                  style={{ position: "absolute", top: 4, right: 4, minHeight: 32, padding: "0 var(--space-2)", fontSize: "0.75rem" }}
                >
                  Suppr.
                </button>
              </div>
            ))}
          </div>
          {hasMore && (
            <button type="button" className="btn btn-secondary" onClick={loadMedia} disabled={loadingMedia}>
              {loadingMedia ? "Chargement…" : "Charger plus"}
            </button>
          )}
        </div>

        <div className="card">
          <h2>Zone dangereuse</h2>
          <button type="button" className="btn btn-danger" onClick={handleDeleteEvent}>
            Supprimer cet événement et tous ses médias
          </button>
        </div>
      </div>
    </Layout>
  );
}
